import { connect, FormikValues } from 'formik'
import { useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { Button } from '@/components/Button'
import { AddCircle, Delete } from '@/components/Icons'
import { FiltersProps, RuleProps } from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/Boolean/Conditions/interfaces'
import { newFilter, Operator } from '@/pages/SpaceAppBuilder/FeaturesPage/Operations/Boolean/Conditions/utils'
import { isVariable } from '@/pages/SpaceAppBuilder/utilities/variableHelpers'

import { Filter } from './Filter'

const FilterDelete = styled.div`
  margin: auto 0.25rem;
  cursor: pointer;
  min-width: 0;
  &:hover {
    g {
      fill: #db3939;
    }
  }
`

export const Rule = connect(
  ({
    formik,
    name,
    condition,
    variables,
    readOnly = false,
    leftComparatorAsTextInput,
    leftComparatorPlaceholder
  }: RuleProps & FormikValues) => {
    return useMemo(
      () => (
        <div className="mt-1">
          <Expression filters={condition.filters} formik={formik} name={`${name}.expression`} />
          <Filters
            formik={formik}
            filters={condition.filters}
            name={`${name}.filters`}
            variables={variables}
            readOnly={readOnly}
            leftComparatorAsTextInput={leftComparatorAsTextInput}
            leftComparatorPlaceholder={leftComparatorPlaceholder}
          />
        </div>
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [condition.filters, name, formik, variables]
    )
  }
)

const Expression = ({ filters, formik, name }: FiltersProps & FormikValues) => {
  const { setFieldValue } = formik
  const isBoolean = (string: string) => string === 'true' || string === 'false'
  useEffect(() => {
    if (filters) {
      const expressions: Array<any> = filters.reduce(
        (prev: any, filter: { leftComparator: any; operator: any; rightComparator: any }) => {
          // eslint-disable-next-line prefer-const
          let { leftComparator, operator, rightComparator } = filter
          if (rightComparator) {
            rightComparator = rightComparator.replaceAll(/["']/g, '')
          }
          let expressionValue = `${leftComparator} ${operator}`
          if (operator !== Operator.PR) {
            const convertedRightComparator =
              Number.isNaN(Number(rightComparator)) && !isBoolean(rightComparator) && !isVariable(rightComparator)
                ? JSON.stringify(rightComparator)
                : rightComparator
            expressionValue += ` ${convertedRightComparator}`
          }
          if (expressionValue) return [...prev, expressionValue]
          else return prev
        },
        [] as string[]
      )
      setFieldValue(name, expressions.join(' and '))
    }
  }, [name, filters, setFieldValue])
  return null
}

const Filters = ({
  name,
  filters,
  formik,
  variables,
  readOnly = true,
  leftComparatorAsTextInput,
  leftComparatorPlaceholder
}: FiltersProps & FormikValues) => {
  return (
    <div className="divide-y divide-slate-200 px-2">
      {filters.map((filter, filterIndex) => {
        return (
          <div key={`filter-${filterIndex}`}>
            <div className="flex items-center justify-between">
              <Filter
                leftComparatorAsTextInput={leftComparatorAsTextInput}
                leftComparatorPlaceholder={leftComparatorPlaceholder}
                renderErrors={false}
                name={`${name}[${filterIndex}]`}
                filterData={filter}
                variables={variables}
                readOnly={readOnly}
                firstColumn={filterIndex > 0 ? 'and' : 'If'}
              />
              {filters.length > 1 && !readOnly && (
                <FilterDelete
                  onClick={() =>
                    formik.setFieldValue(
                      name,
                      filters.filter((_, idx) => idx !== filterIndex)
                    )
                  }
                >
                  <Delete />
                </FilterDelete>
              )}
            </div>
          </div>
        )
      })}
      {!readOnly && (
        <div className="m-1">
          <Button color="transparent" onClick={() => formik.setFieldValue(name, [...filters, newFilter()])}>
            <div className="flex items-center space-x-2 text-blue-600">
              <AddCircle className="mr-1" />
              Add an AND condition
            </div>
          </Button>
        </div>
      )}
    </div>
  )
}
